import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { MetricsList } from '@/features/locations/constants';
import { metersToKilometersString } from '@/features/locations/utils/radiusHelpers';
import { useManualPin } from '@/features/map/context/ManualPinContext';
import { useMixpanel } from '@/hooks/useMixpanel';
import { ACTION_TRACKED } from '@/libs/mixpanel';
import {
  type ILocationMarkersCache,
  useAddWorkspaceLocations,
  useRemoveWorkspaceLocation,
  workspacesKeyFactory,
} from '@/features/workspaces/services/workspace.api';

interface RecomputeParams {
  workspaceId: string;
  /** The location being replaced — deleted once the recompute succeeds. */
  previousLocationId: string;
  coordinates: { latitude: number; longitude: number };
  /** The location's radius before this recompute — Mixpanel attribution only. */
  previousRadius: number;
  radius: number;
  metrics: MetricsList[];
}

/**
 * Re-run a compute for a location at a NEW radius, keeping the SAME metrics.
 *
 * There is no in-place recompute endpoint, so this reuses the add-location
 * endpoint (which always creates a fresh compute) to produce a new location at
 * the same coordinates: the coincident-pin z-order renders it on top of the
 * older one and the shared SSE follow (registered on the surface) auto-selects
 * it once the compute completes. The superseded location is then deleted
 * (silently — this is an implementation detail of "update", not a user
 * delete) so the workspace's location count doesn't double for what is
 * conceptually a single update.
 *
 * `onLaunched` selects the new tempId immediately, mirroring
 * `useLaunchLocationCompute`: the SSE completion handler in
 * `useWorkspaceCompute` only auto-selects a finished run when the currently
 * selected location is still that run's tempId, treating any mismatch as
 * "the user navigated away". Without selecting the tempId up front, the
 * still-selected `previousLocationId` (already deleted by the time the run
 * completes) trips that guard and the popup is stuck on its skeleton.
 */
export function useRecomputeAtRadius(onLaunched: (locationId: string) => void) {
  const { mutate } = useAddWorkspaceLocations();
  const { mutate: removeLocation } = useRemoveWorkspaceLocation();
  const { markPending, clearPending, registerComputeRun } = useManualPin();
  const trackAction = useMixpanel((s) => s.trackAction);
  const queryClient = useQueryClient();

  return useCallback(
    ({
      workspaceId,
      previousLocationId,
      coordinates,
      previousRadius,
      radius,
      metrics,
    }: RecomputeParams) => {
      const tempId = crypto.randomUUID();

      // Snapshot BEFORE the optimistic insert — includes previousLocationId,
      // which is exactly the point: the new location shares its coordinates
      // (see the class doc above), so excluding it is what lets
      // useWorkspaceCompute resolve the NEW location instead of the one being
      // superseded.
      const existingMarkerIds =
        queryClient
          .getQueryData<ILocationMarkersCache>(
            workspacesKeyFactory.locationMarkers(workspaceId).queryKey,
          )
          ?.markers.map((m) => m.id) ?? [];

      onLaunched(tempId);
      markPending([tempId]);

      mutate(
        {
          workspaceId,
          data: { metrics, locations: [{ coordinates, radius }] },
          tempIds: [tempId],
          successMessage: `Recomputing at ${metersToKilometersString(radius)}…`,
        },
        {
          onSuccess: (project) => {
            registerComputeRun({
              projectId: project.id,
              workspaceId,
              tempIds: [tempId],
              autoSelectSingle: true,
              coordinates,
              existingMarkerIds,
            });
            removeLocation({ workspaceId, locationId: previousLocationId, silent: true });
            trackAction(ACTION_TRACKED.LOCATION_RADIUS_CHANGED, {
              previous_radius: previousRadius,
              radius,
            });
          },
          onError: () => {
            clearPending([tempId]);
            onLaunched(previousLocationId);
          },
        },
      );
    },
    [
      mutate,
      removeLocation,
      markPending,
      clearPending,
      registerComputeRun,
      onLaunched,
      trackAction,
      queryClient,
    ],
  );
}
